import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useUsers } from "../../../../../../reactQuery/users";
import { getResponseData } from "../../../../../../reactQuery/response";
import { assignUser } from "../../../../../../services/apiBoards";
import useToastPromise from "../../../../../../hooks/useToast";
import useBoardIdByKey from "../../../../../../hooks/useBoardIdByKey";
import { boardUserFormSchema } from "../../../../../../schemas/boardUserFormSchema";
import { filterUserOptions } from "../userOptionsHelper";
import {
  BOARD_USER_ROLES,
  buildPermissionValues,
  getActiveUserOptions,
  getFormDefaultValues,
  getRoleFromUser,
} from "../utils/boardUserFormUtils";

export const useBoardUserForm = ({
  onClose,
  boardUsers,
  isColabrotar,
  handleAddColabrotor,
  setOpen,
  collaboratorOptions,
  currentUser,
  isUpdateUser,
}) => {
  const { id } = useBoardIdByKey();
  const queryClient = useQueryClient();
  const { showToastPromise } = useToastPromise();
  const { users } = useUsers();

  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSelectedRole, setIsSelectedRole] = useState("User");

  const usersData = useMemo(() => getResponseData(users, []), [users]);
  const userOption = useMemo(() => getActiveUserOptions(usersData), [usersData]);
  const updatedUserOptions = useMemo(
    () => filterUserOptions(userOption, boardUsers, currentUser),
    [boardUsers, currentUser, userOption]
  );
  const newBoardUser = useMemo(
    () => (isColabrotar ? collaboratorOptions : []),
    [collaboratorOptions, isColabrotar]
  );
  const currentUserProfile = useMemo(
    () => boardUsers.find((user) => user?.user_id == currentUser?.user_id),
    [boardUsers, currentUser?.user_id]
  );

  const methods = useForm({
    defaultValues: getFormDefaultValues({
      boardId: id,
      currentUser,
      selectedRole: isSelectedRole,
      isColabrotar,
      isUpdateUser,
    }),
    resolver: yupResolver(boardUserFormSchema),
  });
  const { setValue } = methods;

  useEffect(() => {
    if (isColabrotar) return;

    const currentRole = getRoleFromUser(currentUser);
    setIsSelectedRole(currentRole);
    setValue("user_id", currentUser?.user_id);
  }, [currentUser?.is_admin, currentUser?.user_id, isColabrotar, setValue]);

  useEffect(() => {
    if (isColabrotar) return;

    const newValues = {
      board_id: id,
      user_role: isSelectedRole,
      ...buildPermissionValues({
        selectedRole: isSelectedRole,
        currentUser,
        isUpdateUser,
      }),
    };

    Object.entries(newValues).forEach(([key, value]) => {
      setValue(key, value);
    });
  }, [isSelectedRole, setValue, id, currentUser, isColabrotar, isUpdateUser]);

  const { mutate: assignUserMutate } = useMutation({
    mutationFn: async (data) => await showToastPromise(assignUser, data),
    onSuccess: () => {
      const queries = [
        ["boardUsers", id],
        ["boardName", id],
        ["boards"],
        ["boardUser", currentUser?.user_id],
      ];
      queries.forEach((query) =>
        queryClient.invalidateQueries({ queryKey: query })
      );
      setLoading(false);
      onClose();
    },
    onError: () => {
      setLoading(false);
    },
  });

  const submit = (data) => {
    setApiError("");
    setLoading(true);

    if (!isColabrotar) {
      assignUserMutate(data);
      return;
    }

    handleAddColabrotor(data?.user_id);
    setOpen(false);
  };

  return {
    apiError,
    currentUserProfile,
    handleSubmit: methods.handleSubmit,
    isSelectedRole,
    loading,
    methods,
    newBoardUser,
    roles: BOARD_USER_ROLES,
    setIsSelectedRole,
    submit,
    updatedUserOptions,
  };
};
